import { colorSelectorDescriptor } from './ColorSelector';
import { printOptionsDescriptor } from './PrintOptions';
import { referenceProjectsDescriptor } from './ReferenceProjects';
import { reviewMethodDescriptor } from './ReviewMethodSelector';
import { styleTilesDescriptor } from './StyleTiles';
import type { ColorSelectorOptions, ColorSelectorReply } from './ColorSelector';
import type { PrintOptionsQuestion, PrintOptionsReply } from './PrintOptions';
import type { ReferenceProjectsOptions, ReferenceProjectsReply } from './ReferenceProjects';
import type { ReviewMethodOptions, ReviewMethodReply } from './ReviewMethodSelector';
import type { StyleTilesOptions, StyleTilesReply } from './StyleTiles';
import type { GenerativeUiDescriptor, GenerativeUiType } from './types';

// A single shared TReply for the satisfies constraint below (e.g. `never` or `unknown`) fails to
// typecheck under strictFunctionTypes for any real descriptor, and would erase each descriptor's
// concrete reply type if it did. Mapping each key to its own reply type keeps the check exact.
type GenerativeUiReplyMap = {
    'style-tiles': StyleTilesReply;
    'color-selector': ColorSelectorReply;
    'product-options': PrintOptionsReply;
    'reference-projects': ReferenceProjectsReply;
    'review-method': ReviewMethodReply;
};

// Same rationale as GenerativeUiReplyMap above, for the composer-side options type.
type GenerativeUiOptionsMap = {
    'style-tiles': StyleTilesOptions;
    'color-selector': ColorSelectorOptions;
    'product-options': PrintOptionsQuestion;
    'reference-projects': ReferenceProjectsOptions;
    'review-method': ReviewMethodOptions;
};

// Keys are the WIRE type strings carried in a message's parsed `type` field (e.g.
// 'style-tiles'), not the component folder name (StyleTiles/). The two are free to diverge.
export const GENERATIVE_UI_REGISTRY = {
    'style-tiles': styleTilesDescriptor,
    'color-selector': colorSelectorDescriptor,
    'product-options': printOptionsDescriptor,
    'reference-projects': referenceProjectsDescriptor,
    'review-method': reviewMethodDescriptor,
} as const satisfies {
    [K in GenerativeUiType]: GenerativeUiDescriptor<
        K,
        GenerativeUiOptionsMap[K],
        GenerativeUiReplyMap[K]
    >;
};

export type GenerativeUiRegistryEntry =
    (typeof GENERATIVE_UI_REGISTRY)[keyof typeof GENERATIVE_UI_REGISTRY];

/**
 * A `{ type, data }` pair per registered entry, `data` narrowed to that entry's concrete reply
 * type. Lets a consumer such as `GenerativeUiDisplayMessage` carry a typed payload instead of
 * `{ data: unknown }`.
 */
export type GenerativeUiPayload = {
    [K in keyof typeof GENERATIVE_UI_REGISTRY]: {
        type: NonNullable<
            ReturnType<(typeof GENERATIVE_UI_REGISTRY)[K]['parseDisplayPayload']>
        >['type'];
        data: NonNullable<
            ReturnType<(typeof GENERATIVE_UI_REGISTRY)[K]['parseDisplayPayload']>
        >['data'];
    };
}[keyof typeof GENERATIVE_UI_REGISTRY];

function isGenerativeUiType(type: string): type is GenerativeUiType {
    // hasOwnProperty, not `in`: `in` also matches inherited properties (e.g. 'toString'), which
    // would resolve to a descriptor for a type that was never registered.
    return Object.prototype.hasOwnProperty.call(GENERATIVE_UI_REGISTRY, type);
}

/**
 * Looks up a descriptor by wire type. `type` is `string`, not `GenerativeUiType`, because
 * callers hold an unvalidated value parsed from JSON, not one already known to be registered.
 */
export function findGenerativeUiDescriptor(type: string): GenerativeUiRegistryEntry | undefined {
    return isGenerativeUiType(type) ? GENERATIVE_UI_REGISTRY[type] : undefined;
}
